import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { ExistsFilterOperator } from "@redotech/redo-views-common/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  FilterOperator,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { createEnum } from "@redotech/zod-util/zod-enum";
import { AdvancedFilterData, FilterGroup } from "../views/views";

export enum InventorySource {
  SELLABLE = "sellable",
  NON_SELLABLE = "non_sellable",
}

export const DEFAULT_INVENTORY_SOURCE = InventorySource.SELLABLE;

export const InventorySourceSchema = createEnum(InventorySource, {
  enumName: "InventorySource",
});

export function normalizeInventorySource(
  inventorySource: InventorySource | null | undefined,
): InventorySource {
  return inventorySource ?? DEFAULT_INVENTORY_SOURCE;
}

export function getInventorySourceFilterGroup(
  inventorySource: InventorySource | null | undefined,
): FilterGroup {
  const source = normalizeInventorySource(inventorySource);
  const sourceFilter = getInventorySourceValueFilter(source);
  const oppositeSource =
    source === InventorySource.SELLABLE
      ? InventorySource.NON_SELLABLE
      : InventorySource.SELLABLE;

  if (source === InventorySource.NON_SELLABLE) {
    return {
      operator: FilterOperator.AND,
      clauses: [
        sourceFilter,
        getInventorySourceValueFilter(
          oppositeSource,
          ArrayStringFilterOperator.NONE_OF,
        ),
      ],
    };
  }

  return {
    operator: FilterOperator.AND,
    clauses: [
      {
        operator: FilterOperator.OR,
        clauses: [
          sourceFilter,
          {
            name: "inventorySource",
            type: AdvancedFilterType.EXISTS,
            value: true,
            operator: ExistsFilterOperator.NOT_EXISTS,
          },
        ],
      },
      getInventorySourceValueFilter(
        oppositeSource,
        ArrayStringFilterOperator.NONE_OF,
      ),
    ],
  };
}

export function isInventorySourceSellable(
  inventorySource: InventorySource | null | undefined,
): boolean {
  return normalizeInventorySource(inventorySource) === InventorySource.SELLABLE;
}

function getInventorySourceValueFilter(
  inventorySource: InventorySource,
  operator = ArrayStringFilterOperator.ANY_OF,
): AdvancedFilterData {
  return {
    name: "inventorySource",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [inventorySource],
    operator,
  };
}
